import React, { useMemo, useState } from "react";
import {
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  Box,
  Typography,
  Button,
} from "@mui/material";
import Paging from "./ActiveUserPaging";
import UserSubscription from "./UserSubscription";
import UserPassportModal from "./UserPassportModal";
import { useNavigate } from "react-router-dom";
import { fetchWithToken } from "../../../api/apiutils";

function formatDate(dt) {
  if (!dt) return "";
  const d = new Date(dt);
  return d.toLocaleDateString("ru-RU", { timeZone: "Asia/Almaty" });
}

function renderSubscriptionPeriodHistory(history) {
  if (!Array.isArray(history) || history.length === 0) return "—";
  return (
    <Box component="div" sx={{ whiteSpace: "pre-line" }}>
      {history
        .map((p) => `${formatDate(p.start)} - ${formatDate(p.end)}`)
        .join("\n")}
    </Box>
  );
}

const SegmentationTable = ({
  data,
  loading,
  error,
  total,
  page,
  pageLimit,
  onPageChange,
  actionDateFrom,
}) => {
  const [subOpen, setSubOpen] = useState(false);
  const [userName, setUserName] = useState("");

  const [passportOpen, setPassportOpen] = useState(false);
  const [passportItem, setPassportItem] = useState(null);

  const [analysingBin, setAnalysingBin] = useState(null);

  const rowsPerPage = pageLimit || 10;
  const navigate = useNavigate();

  const handleAnalyseAndNavigate = async (bin) => {
    if (analysingBin) return;
    setAnalysingBin(bin);
    try {
      const searchResponse = await fetchWithToken(
        `/api/companies/analyses?Identifier=${bin}&Page=1&PageLimit=10000`,
      );
      const searchResult = await searchResponse.json();

      if (searchResult?.data?.length) {
        const existing = searchResult.data[0];
        navigate(`/active-users/${bin}/${existing.id}`);
        return;
      }

      await createNewAnalysis(bin);
    } catch (error) {
      console.error("Ошибка при поиске существующего анализа:", error);
    } finally {
      setAnalysingBin(null);
    }
  };

  const createNewAnalysis = async (bin) => {
    const newItem = { identifier: bin, type: "OneTime" };
    try {
      const response = await fetchWithToken("/api/companies/analyses", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(newItem),
      });
      if (!response.ok) throw new Error(response.statusText);

      const data = await response.json();
      navigate(`/active-users/${bin}/${data.id}`);
    } catch (error) {
      console.error(error);
    }
  };

  // ВАЖНО: у каждой кнопки stopPropagation, чтобы клик не долетал до TableRow
  function renderIdentifiers(item) {
    if (!item.identifiers.length) return <div>Не указано</div>;

    if (item.identifiers.length === 1) {
      const id = item.identifiers[0];
      return (
        <Button
          disabled={analysingBin === id}
          onClick={(e) => {
            e.stopPropagation();
            handleAnalyseAndNavigate(id);
          }}
        >
          {analysingBin === id ? <CircularProgress size={16} /> : id}
        </Button>
      );
    }
    return (
      <>
        {item.identifiers.map((id) => (
          <Button
            key={id}
            disabled={analysingBin === id}
            onClick={(e) => {
              e.stopPropagation();
              handleAnalyseAndNavigate(id);
            }}
          >
            {analysingBin === id ? <CircularProgress size={16} /> : id}
          </Button>
        ))}
      </>
    );
  }

  const totalPages = useMemo(
    () => Math.max(1, Math.ceil((total || 0) / rowsPerPage)),
    [total, rowsPerPage],
  );

  const handleAddSubscription = (userName) => {
    setUserName(userName);
    setSubOpen(true);
  };

  const handleOpenPassport = (item) => {
    setPassportItem(item);
    setPassportOpen(true);
  };

  const handleClosePassport = () => {
    setPassportOpen(false);
    setPassportItem(null);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Typography color="error">Ошибка: {error}</Typography>;

  return (
    <Box>
      <TableContainer component={Paper} className="user-list-container">
        <Typography variant="h6" gutterBottom>
          Общее количество пользователей: {total || 0}
        </Typography>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Номер телефона</TableCell>
              <TableCell>ОС</TableCell>
              <TableCell>Дата регистрации</TableCell>
              <TableCell>Дата последней активности</TableCell>
              <TableCell>Подписка</TableCell>
              <TableCell>Срок подписки</TableCell>
              <TableCell>БИН/ИИН</TableCell>
              <TableCell>История подписки</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length ? (
              data.map((item, index) => (
                <TableRow
                  hover
                  onClick={() => handleOpenPassport(item)}
                  key={item.user && item.user.id ? item.user.id : index}
                  sx={{
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ color: "#047bf3ff", cursor: "pointer" }}>
                    <span
                      onClick={(e) => {
                        e.stopPropagation();
                        if (window.confirm("Добавить подписку?")) {
                          handleAddSubscription(item.userName || "Нет данных");
                        }
                      }}
                    >
                      {item.userName || "Нет данных"}
                    </span>
                  </TableCell>

                  <TableCell>{item.operatingSystem}</TableCell>
                  <TableCell>
                    {item.dateOfRegistration
                      ? new Date(item.dateOfRegistration).toLocaleString(
                          "ru-RU",
                          {
                            dateStyle: "short",
                            timeStyle: "short",
                            timeZone: "Asia/Almaty",
                          },
                        )
                      : "Нет данных"}
                  </TableCell>
                  <TableCell>
                    {item.dateOfLastAction
                      ? new Date(item.dateOfLastAction).toLocaleString(
                          "ru-RU",
                          {
                            dateStyle: "short",
                            timeStyle: "short",
                            timeZone: "Asia/Almaty",
                          },
                        )
                      : "Нет данных"}
                  </TableCell>
                  <TableCell>{item.subscription || "Нет данных"}</TableCell>
                  <TableCell>
                    {item.subscriptionDate &&
                    item.subscriptionDate.start &&
                    item.subscriptionDate.end
                      ? `${new Date(
                          item.subscriptionDate.start,
                        ).toLocaleDateString()} - ${new Date(
                          item.subscriptionDate.end,
                        ).toLocaleDateString()}`
                      : "Нет данных"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    {renderIdentifiers(item)}
                  </TableCell>
                  <TableCell>
                    {renderSubscriptionPeriodHistory(
                      item.subscriptionPeriodHistory,
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  Нет данных
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <UserSubscription
        open={subOpen}
        onClose={() => setSubOpen(false)}
        userName={userName}
      />

      <Paging
        localPage={page}
        totalPages={totalPages}
        onPageChange={onPageChange}
      />

      <UserPassportModal
        open={passportOpen}
        onClose={handleClosePassport}
        item={passportItem}
        actionDateFrom={actionDateFrom}
      />
    </Box>
  );
};

export default SegmentationTable;
